/**
 * INNOVDEPOT UTM CAPTURE + GHL INJECTION
 * ========================================
 * Deploy to: Shopify theme.liquid (before </head> or before </body>)
 * Method: Shopify Admin API → Asset endpoint OR paste manually
 *
 * What it does:
 * 1. Captures UTM params + gclid from URL on every page load
 * 2. Stores in first-party cookie (30-day expiry, first-touch preserved)
 * 3. Injects UTM values into GHL chat widget data attributes
 * 4. Injects hidden fields into any <form> on the page
 * 5. Exposes window.__innovUTM for any script to read
 *
 * Cookie: _innov_utm (JSON blob)
 * Cookie: _innov_landing (first landing URL)
 */
(function() {
  'use strict';

  // ============================================================
  // CONFIG
  // ============================================================
  var COOKIE_NAME = '_innov_utm';
  var LANDING_COOKIE = '_innov_landing';
  var COOKIE_DAYS = 30;
  var UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];

  // ============================================================
  // COOKIE HELPERS
  // ============================================================
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = name + '=' + encodeURIComponent(value) +
      ';expires=' + d.toUTCString() +
      ';path=/;SameSite=Lax;Secure';
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) {
      try { return decodeURIComponent(match[2]); } catch(e) { return match[2]; }
    }
    return null;
  }

  // ============================================================
  // URL PARAM EXTRACTION
  // ============================================================
  function getURLParams() {
    var params = {};
    var search = window.location.search.substring(1);
    if (!search) return params;

    var pairs = search.split('&');
    for (var i = 0; i < pairs.length; i++) {
      var pair = pairs[i].split('=');
      var key = decodeURIComponent(pair[0]);
      var val = pair.length > 1 ? decodeURIComponent(pair[1]) : '';
      if (UTM_PARAMS.indexOf(key) !== -1 && val) {
        params[key] = val;
      }
    }
    return params;
  }

  // ============================================================
  // MAIN CAPTURE LOGIC
  // ============================================================
  function captureUTMs() {
    var urlParams = getURLParams();
    var hasNewUTMs = Object.keys(urlParams).length > 0;

    // Read existing cookie
    var existing = {};
    var cookieVal = getCookie(COOKIE_NAME);
    if (cookieVal) {
      try { existing = JSON.parse(cookieVal); } catch(e) { existing = {}; }
    }

    // FIRST-TOUCH attribution: only overwrite if new UTMs present
    // This means the FIRST ad click is preserved even if they come back later
    if (hasNewUTMs) {
      // If no existing data, this is first touch — save everything
      if (!existing.utm_source) {
        existing = urlParams;
        existing._first_touch = new Date().toISOString();
        existing._first_landing = window.location.href;
      }
      // Always update last touch
      existing._last_touch = new Date().toISOString();
      existing._last_landing = window.location.href;
      existing._last_source = urlParams.utm_source || '';
      existing._last_medium = urlParams.utm_medium || '';
      existing._last_campaign = urlParams.utm_campaign || '';

      // Save to cookie
      setCookie(COOKIE_NAME, JSON.stringify(existing), COOKIE_DAYS);
    } else if (!cookieVal) {
      // No UTMs in URL and no existing cookie — record direct visit
      existing = {
        utm_source: '(direct)',
        utm_medium: '(none)',
        _first_touch: new Date().toISOString(),
        _first_landing: window.location.href
      };
      setCookie(COOKIE_NAME, JSON.stringify(existing), COOKIE_DAYS);
    }

    // Always save landing page on first visit
    if (!getCookie(LANDING_COOKIE)) {
      setCookie(LANDING_COOKIE, window.location.href, COOKIE_DAYS);
    }

    // Expose globally for other scripts
    window.__innovUTM = existing;
    return existing;
  }

  // ============================================================
  // GHL CHAT WIDGET INJECTION
  // ============================================================
  function injectGHLChatUTMs(utmData) {
    // GHL chat widget uses window.leadConnector or LC_API
    // We inject UTM data into the chat widget's contact fields
    // This runs on a short interval until the widget loads

    var attempts = 0;
    var maxAttempts = 30; // 15 seconds max

    var interval = setInterval(function() {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(interval);
        return;
      }

      // Method 1: GHL Chat Widget v2 (leadConnector)
      if (window.leadConnector && window.leadConnector.chatWidget) {
        try {
          window.leadConnector.chatWidget.setContactData({
            customField: {
              utm_source: utmData.utm_source || '',
              utm_medium: utmData.utm_medium || '',
              utm_campaign: utmData.utm_campaign || '',
              utm_content: utmData.utm_content || '',
              utm_term: utmData.utm_term || '',
              gclid: utmData.gclid || '',
              landing_page: utmData._first_landing || window.location.href
            }
          });
          clearInterval(interval);
        } catch(e) { /* widget not ready yet */ }
      }

      // Method 2: LC_API (older GHL chat)
      if (window.LC_API && window.LC_API.set_custom_variables) {
        try {
          window.LC_API.set_custom_variables([
            { name: 'utm_source', value: utmData.utm_source || '' },
            { name: 'utm_medium', value: utmData.utm_medium || '' },
            { name: 'utm_campaign', value: utmData.utm_campaign || '' },
            { name: 'utm_content', value: utmData.utm_content || '' },
            { name: 'utm_term', value: utmData.utm_term || '' },
            { name: 'gclid', value: utmData.gclid || '' },
            { name: 'landing_page', value: utmData._first_landing || window.location.href }
          ]);
          clearInterval(interval);
        } catch(e) { /* widget not ready yet */ }
      }
    }, 500);
  }

  // ============================================================
  // FORM HIDDEN FIELD INJECTION
  // ============================================================
  function injectFormUTMs(utmData) {
    // Find all forms on the page and add hidden UTM fields
    // This covers GHL forms, Shopify forms, and any custom forms

    function addHiddenFields(form) {
      // Skip if already injected
      if (form.getAttribute('data-utm-injected')) return;
      form.setAttribute('data-utm-injected', 'true');

      var fields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid'];
      for (var i = 0; i < fields.length; i++) {
        var key = fields[i];
        var val = utmData[key] || '';
        if (val) {
          var input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = val;
          form.appendChild(input);
        }
      }

      // Also add landing page
      if (utmData._first_landing) {
        var lpInput = document.createElement('input');
        lpInput.type = 'hidden';
        lpInput.name = 'landing_page';
        lpInput.value = utmData._first_landing;
        form.appendChild(lpInput);
      }
    }

    // Inject into existing forms
    var forms = document.querySelectorAll('form');
    for (var i = 0; i < forms.length; i++) {
      addHiddenFields(forms[i]);
    }

    // Watch for dynamically added forms (GHL forms load async)
    if (window.MutationObserver) {
      var observer = new MutationObserver(function(mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var nodes = mutations[m].addedNodes;
          for (var n = 0; n < nodes.length; n++) {
            if (nodes[n].tagName === 'FORM') {
              addHiddenFields(nodes[n]);
            }
            // Also check children
            if (nodes[n].querySelectorAll) {
              var childForms = nodes[n].querySelectorAll('form');
              for (var f = 0; f < childForms.length; f++) {
                addHiddenFields(childForms[f]);
              }
            }
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  // ============================================================
  // SHOPIFY CHECKOUT ATTRIBUTION (add UTM to cart attributes)
  // ============================================================
  function injectCartAttributes(utmData) {
    // Shopify allows cart attributes that carry through to the order
    // These appear in order.note_attributes
    if (typeof fetch !== 'undefined' && utmData.utm_source) {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            utm_source: utmData.utm_source || '',
            utm_medium: utmData.utm_medium || '',
            utm_campaign: utmData.utm_campaign || '',
            utm_content: utmData.utm_content || '',
            utm_term: utmData.utm_term || '',
            gclid: utmData.gclid || '',
            landing_page: utmData._first_landing || ''
          }
        })
      }).catch(function() { /* silent fail — non-critical */ });
    }
  }

  // ============================================================
  // INIT
  // ============================================================
  function init() {
    var utmData = captureUTMs();

    // Only inject if we have meaningful UTM data
    if (utmData && utmData.utm_source) {
      injectGHLChatUTMs(utmData);
      injectFormUTMs(utmData);
      injectCartAttributes(utmData);
    } else {
      // Still inject form watcher for direct visitors
      injectFormUTMs(utmData || {});
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also run cart attributes update when items are added to cart
  // (in case UTMs were captured after initial page load)
  document.addEventListener('click', function(e) {
    var target = e.target;
    // Check if click was on an add-to-cart button
    if (target && (
      target.matches('[name="add"], .btn-addtocart, .add-to-cart, [data-add-to-cart]') ||
      target.closest('[name="add"], .btn-addtocart, .add-to-cart, [data-add-to-cart]')
    )) {
      var utmData = window.__innovUTM;
      if (utmData && utmData.utm_source) {
        // Small delay to let cart update first
        setTimeout(function() { injectCartAttributes(utmData); }, 500);
      }
    }
  });
})();
